import { ReactNode } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { MRT_ColumnDef as MRTColumnDef, MRT_Row as MRTRow } from 'material-react-table';
import { Box } from '@mui/material';
import type { Column } from '../DataGridHandler';
import getTextWidth from './getWidth';

type RowData = Record<string, any>;

export const ESTIMATED_ROW_HEIGHT = 34;

export default function buildColumns(
  columns: Column<any>[],
  actions: Record<string, any>,
  context: any,
  headerFontSize: number,
  headerFont: string,
  tableFontSize: number,
  firstColLeftPaddingPx: number
): MRTColumnDef<RowData, ReactNode>[] {
  const baseCols: MRTColumnDef<RowData, ReactNode>[] = columns
    .filter(col => col.isAvailable())
    .map((col: Column<any>): MRTColumnDef<RowData, ReactNode> => {
      const getRaw = (row: RowData) => col.getValue(row);
      const sample = getRaw({} as RowData);
      const isNumeric = typeof sample === 'number';

      return {
        id: col.id,
        accessorFn: (row: RowData) => {
          const v = getRaw(row);
          if (v == null) return null;
          return isNumeric ? Number(v) : String(v);
        },
        sortingFn: isNumeric ? 'basic' : 'text',
        header: col.getTitle(),
        Header: () => (
          <Tooltip title={col.getDescription()}>
            <span>{col.getTitle()}</span>
          </Tooltip>
        ),
        minSize:
          firstColLeftPaddingPx +
          getTextWidth(col.getTitle(), `${headerFontSize}px ${headerFont}`) +
          18 +
          32,
        size: col.getWidth().px,
        enableSorting: true,
        Cell: ({ row }: { row: MRTRow<RowData> }) => (
          <Box
            sx={{
              height: `${ESTIMATED_ROW_HEIGHT}px`,
              verticalAlign: 'middle',
              padding: '7px 0px',
              display: 'inline-block',
              boxSizing: 'border-box',
              maxWidth: '100%',
              overflow: 'hidden',
              fontSize: tableFontSize,
              textOverflow: 'ellipsis',
              '&:hover': {
                maxWidth: 'unset',
                overflow: 'visible',
              },
            }}
          >
            {col.getCell(row.original, row)}
          </Box>
        ),
      };
    });

  const actionCols: MRTColumnDef<RowData, ReactNode>[] = Object.entries(actions).map(
    ([columnId, action]): MRTColumnDef<RowData, ReactNode> => ({
      id: `action_${columnId}`,
      header: '',
      enableSorting: false,
      Cell: ({ row }: { row: MRTRow<RowData> }) =>
        action.isVisible({ row: row.original, context }) ? (
          <action.Button row={row.original} context={context} />
        ) : null,
    })
  );

  return [...baseCols, ...actionCols];
}
